import { registerDestructor } from '@ember/destroyable';
import { type Registry as Services, service } from '@ember/service';
import Modifier from 'ember-modifier';
import createCompatibilityRouter from 'lumen/utils/mount-react/create-compatibility-router';
import createIntl from 'lumen/utils/mount-react/create-intl';
import {
  mount,
  type MountableComponent,
  type MountHandle,
} from 'shared-react/lumen';

interface MountReactSignature<Props extends object> {
  Args: {
    Positional: [
      component: MountableComponent<Props>,
      ...props: [props?: Props],
    ];
  };
  Element: Element;
}

/**
 * The single seam between Ember and shared-react: mount an opaque
 * MountableComponent (unmounting it on teardown), backing shared-react's
 * router and intl contracts with Ember's RouterService and ember-intl. All
 * React bootstrapping lives in shared-react's self-contained build, so lumen
 * needs no React dependency of its own.
 */
export default class MountReact<Props extends object> extends Modifier<
  MountReactSignature<Props>
> {
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];

  #handle?: MountHandle<Props>;

  modify(
    element: Element,
    positional: MountReactSignature<Props>['Args']['Positional'],
  ): void {
    const [component, props] = positional;

    if (!this.#handle) {
      this.#handle = mount(element, component, {
        intl: createIntl(this.intl),
        router: createCompatibilityRouter(this.router),
        props,
      });
      registerDestructor(this, () => this.#handle?.unmount());
    } else {
      this.#handle.render(props);
    }
  }
}
